import { useState } from "react";
import {
  View,
  Text,
  Pressable,
  TextInput,
  Image,
  Linking,
  Platform,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import {
  haptic,
  Dialog,
  MoonCheckbox,
  DialogPrimaryButton,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import { useStytchUser } from "@louise/api/useStytch";
import { EMBER } from "../../../../onboarding/theme";
import {
  useEnrollPasskey,
  useSetup2FA,
  useVerify2FA,
} from "@src/packages/auth/hooks/use2FA";
import { useTranslations } from "@src/packages/i18n";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

const ISSUER = "Louise";

function chunkSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

function buildOtpauthUri(secret: string, label: string): string {
  const encodedLabel = encodeURIComponent(`${ISSUER}:${label}`);
  const params = new URLSearchParams({
    secret,
    issuer: ISSUER,
  });
  return `otpauth://totp/${encodedLabel}?${params.toString()}`;
}

type Props = {
  visible: boolean;
  onClose: () => void;
  onComplete: () => void;
};

type Step = "intro" | "scan" | "verify" | "recovery";

export default function Setup2FAModal({ visible, onClose, onComplete }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("Setup2FAModal");
  const [step, setStep] = useState<Step>("intro");
  const [totpCode, setTotpCode] = useState("");
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCode: string;
    recoveryCodes: string[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const { user } = useStytchUser();
  const accountLabel = user?.emails?.[0]?.email ?? user?.user_id ?? "account";

  const setup2FA = useSetup2FA();
  const verify2FA = useVerify2FA();
  const enrollPasskey = useEnrollPasskey();

  const reset = () => {
    setStep("intro");
    setTotpCode("");
    setSetupData(null);
    setError(null);
    setRecoveryAcknowledged(false);
    setSecretCopied(false);
  };

  const handleClose = () => {
    // On the recovery step the codes are only shown once. Don't let an
    // accidental backdrop tap or back-button discard them.
    if (step === "recovery") return;
    reset();
    onClose();
  };

  const handleOpenInAuthenticator = async () => {
    if (!setupData) return;
    const uri = buildOtpauthUri(setupData.secret, accountLabel);
    const supported = await Linking.canOpenURL(uri).catch(() => false);
    if (supported) {
      await Linking.openURL(uri);
      haptic("buzz");
    } else {
      // No authenticator app installed — copy the secret so the user can
      // paste it once they install one. Show feedback so the tap doesn't
      // feel like a no-op.
      await Clipboard.setStringAsync(setupData.secret);
      setSecretCopied(true);
      haptic("buzz");
      setTimeout(() => setSecretCopied(false), 3000);
    }
  };

  const handleSetup = async () => {
    try {
      setError(null);
      const data = await setup2FA.mutateAsync();
      setSetupData(data);
      setStep("scan");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("you.modals.setup_2fa.intro_error_default"),
      );
    }
  };

  // Passkey enrollment is an alternative to the TOTP flow, not a step within
  // it: one WebAuthn registration gives the account a "hardware" second factor
  // (moon's SMS first factor is "possession"), which is enough to enroll in
  // MFA. On success we finish the modal the same way handleDone does — there
  // are no recovery codes to display for a passkey.
  const handleEnrollPasskey = async () => {
    try {
      setError(null);
      await enrollPasskey.mutateAsync();
      haptic("buzz");
      reset();
      onComplete();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("you.modals.setup_2fa.passkey_error_default"),
      );
    }
  };

  const handleVerify = async () => {
    try {
      setError(null);
      await verify2FA.mutateAsync({ totpCode });
      haptic("buzz");
      setStep("recovery");
    } catch {
      setError(t("you.modals.setup_2fa.verify_error_invalid"));
    }
  };

  const handleCopyRecoveryCodes = async () => {
    if (setupData?.recoveryCodes) {
      await Clipboard.setStringAsync(setupData.recoveryCodes.join("\n"));
      haptic("buzz");
    }
  };

  const handleDone = () => {
    haptic("buzz");
    reset();
    onComplete();
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      closeButton={step === "recovery" ? false : "with-background"}
      dismissOnBackdropTap={step !== "recovery"}
      backdropTestID="setup-2fa-backdrop"
      layout="centered"
    >
      {step === "intro" && (
        <>
          <Text
            testID="setup-2fa-intro-title"
            accessibilityRole="header"
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "600",
              color: EMBER.colors.text,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {t("you.modals.setup_2fa.intro_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              textAlign: "center",
              lineHeight: 20,
              marginBottom: 24,
            }}
          >
            {t("you.modals.setup_2fa.intro_message")}
          </Text>
          {error && (
            <Text
              style={{
                fontSize: 13,
                color: EMBER.colors.coral,
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              {error}
            </Text>
          )}
          <View style={{ flexDirection: "row", gap: 12 }}>
            <SoftSecondaryButton
              label={t("you.modals.setup_2fa.intro_cancel")}
              onPress={handleClose}
              flex={1}
            />
            <DialogPrimaryButton
              label={t("you.modals.setup_2fa.intro_setup")}
              onPress={handleSetup}
              loading={setup2FA.isPending}
              flex={1}
            />
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginVertical: 16,
            }}
          >
            <View
              style={{
                flex: 1,
                height: 1,
                backgroundColor: EMBER.colors.border,
              }}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textFaint,
                marginHorizontal: 12,
              }}
            >
              {t("you.modals.setup_2fa.passkey_or")}
            </Text>
            <View
              style={{
                flex: 1,
                height: 1,
                backgroundColor: EMBER.colors.border,
              }}
            />
          </View>

          <SoftSecondaryButton
            onPress={handleEnrollPasskey}
            disabled={enrollPasskey.isPending}
            label={t("you.modals.setup_2fa.passkey_button")}
            testID="passkey-enroll-button"
            style={{
              opacity: enrollPasskey.isPending ? 0.6 : 1,
            }}
          />
        </>
      )}

      {step === "scan" && setupData && (
        <>
          <Text
            accessibilityRole="header"
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "600",
              color: EMBER.colors.text,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {t("you.modals.setup_2fa.scan_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              textAlign: "center",
              lineHeight: 20,
              marginBottom: 16,
            }}
          >
            {Platform.OS === "web"
              ? t("you.modals.setup_2fa.scan_message_web")
              : t("you.modals.setup_2fa.scan_message_mobile")}
          </Text>
          {Platform.OS !== "web" && (
            <>
              <SoftSecondaryButton
                onPress={handleOpenInAuthenticator}
                label={t("you.modals.setup_2fa.scan_open_in_app")}
                style={{
                  marginBottom: secretCopied ? 4 : 12,
                }}
              />
              {secretCopied && (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    color: EMBER.colors.textLight,
                    textAlign: "center",
                    marginBottom: 12,
                  }}
                >
                  {t("you.modals.setup_2fa.scan_secret_copied")}
                </Text>
              )}
            </>
          )}
          <View style={{ alignItems: "center", marginBottom: 16 }}>
            <Image
              source={{ uri: setupData.qrCode }}
              style={{
                width: 200,
                height: 200,
                borderRadius: 8,
                backgroundColor: "#fff",
              }}
              accessibilityLabel={t("you.modals.setup_2fa.scan_qr_a11y")}
            />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textFaint,
              textAlign: "center",
              marginBottom: 4,
            }}
          >
            {t("you.modals.setup_2fa.scan_manual_intro")}
          </Text>
          <Pressable
            onPress={async () => {
              // The one control in the app that does NOT log its visible
              // copy: that copy is the TOTP secret, an auth credential. A
              // credential never reaches analytics, so a static descriptor
              // stands in for it here and nowhere else.
              capturePress("copy_totp_secret");
              await Clipboard.setStringAsync(setupData.secret);
              haptic("buzz");
            }}
          >
            <Text
              className="font-dm-sans"
              selectable
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: EMBER.colors.warm,
                textAlign: "center",
                marginBottom: 20,
                letterSpacing: 1,
                fontVariant: ["tabular-nums"],
              }}
            >
              {chunkSecret(setupData.secret)}
            </Text>
          </Pressable>
          <DialogPrimaryButton
            onPress={() => setStep("verify")}
            label={t("you.modals.setup_2fa.scan_next")}
            fullWidth
          />
        </>
      )}

      {step === "verify" && (
        <>
          <Text
            accessibilityRole="header"
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "600",
              color: EMBER.colors.text,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {t("you.modals.setup_2fa.verify_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              textAlign: "center",
              lineHeight: 20,
              marginBottom: 20,
            }}
          >
            {t("you.modals.setup_2fa.verify_message")}
          </Text>
          <TextInput
            value={totpCode}
            onChangeText={(text) => {
              setTotpCode(text.replace(/\D/g, "").slice(0, 6));
              setError(null);
            }}
            placeholder={t("you.modals.setup_2fa.verify_code_placeholder")}
            keyboardType="number-pad"
            maxLength={6}
            style={{
              fontSize: 24,
              fontWeight: "600",
              textAlign: "center",
              letterSpacing: 8,
              paddingVertical: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: error ? EMBER.colors.coral : EMBER.colors.border,
              color: EMBER.colors.text,
              marginBottom: 8,
            }}
          />
          {error && (
            <Text
              style={{
                fontSize: 13,
                color: EMBER.colors.coral,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              {error}
            </Text>
          )}
          <View style={{ height: 8 }} />
          <DialogPrimaryButton
            onPress={handleVerify}
            disabled={totpCode.length !== 6 || verify2FA.isPending}
            loading={verify2FA.isPending}
            label={t("you.modals.setup_2fa.verify_confirm")}
            fullWidth
          />
        </>
      )}

      {step === "recovery" && setupData && (
        <>
          {/*
           * The recovery step is only rendered after `verify2FA` resolves,
           * which requires BOTH the client-side `stytch.totp.authenticate`
           * AND the server-side `POST /me/2fa/enroll` to succeed — i.e.
           * enrollment is fully complete. This testID gives that completed
           * state a stable DOM anchor so it is observable without depending
           * on the Stytch SDK's reactive user state propagating to the
           * account row badge (louise-patient-expo#1466). Inert: no visible
           * or behavioral effect.
           */}
          <Text
            testID="setup-2fa-enrolled"
            accessibilityRole="header"
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "600",
              color: EMBER.colors.text,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {t("you.modals.setup_2fa.recovery_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              textAlign: "center",
              lineHeight: 20,
              marginBottom: 16,
            }}
          >
            {t("you.modals.setup_2fa.recovery_message")}
          </Text>
          <View
            testID="setup-2fa-recovery-codes"
            style={{
              backgroundColor: EMBER.colors.warmDark + "10",
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
          >
            {setupData.recoveryCodes.map((code) => (
              <Text
                key={code}
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: EMBER.colors.text,
                  textAlign: "center",
                  lineHeight: 24,
                  fontVariant: ["tabular-nums"],
                }}
              >
                {code}
              </Text>
            ))}
          </View>
          <SoftSecondaryButton
            onPress={handleCopyRecoveryCodes}
            label={t("you.modals.setup_2fa.recovery_copy_all")}
            style={{
              marginBottom: 12,
            }}
          />

          <MoonCheckbox
            value={recoveryAcknowledged}
            onChange={setRecoveryAcknowledged}
            accentColor={EMBER.colors.warm}
            label={t("you.modals.setup_2fa.recovery_acknowledge")}
            testID="setup-2fa-recovery-acknowledge"
            style={{ paddingVertical: 8, marginBottom: 12 }}
          />

          <DialogPrimaryButton
            onPress={handleDone}
            disabled={!recoveryAcknowledged}
            label={t("you.modals.setup_2fa.recovery_done")}
            fullWidth
          />
        </>
      )}
    </Dialog>
  );
}
